import React, { useState, useMemo } from 'react';

import { CheckIcon, CrossIcon } from '@vlabs/icons';
import { Control } from '@vlabs/uikit';

import st from '../OptionsFilter.module.sass';

interface DynamicOptionInputProps {
  defaultValue?: string | undefined;
  error?: string | undefined;
  onSubmit: (value: string) => void;
  onCancel: VoidFunction;
  validate?: ((value: string) => string | boolean) | undefined;
}

export function DynamicOptionInput({
  onSubmit,
  onCancel,
  defaultValue = '',
  error = undefined,
  validate = undefined,
}: DynamicOptionInputProps): React.JSX.Element {
  const [value, setValue] = useState(defaultValue);
  const [validationError, setValidationError] = useState<string | undefined>(undefined);

  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = e.target.value;

    setValue(nextValue);

    const validationResult = validate?.(nextValue.trim());
    setValidationError(typeof validationResult === 'string' ? validationResult : undefined);
  };

  const onSubmitHandler = () => {
    const trimmedValue = value.trim();

    if (!trimmedValue || validationError) {
      return;
    }

    onSubmit(trimmedValue);
  };

  const errorMessage = useMemo(
    () => validationError ?? error,
    [error, validationError],
  );

  return (
    <div className={st.DynamicOptionInputRoot}>
      <div>
        <Control.Input
          data-testid="newOption"
          errorMessage={errorMessage}
          onChange={onChangeHandler}
          size="small"
          value={value}
        />
      </div>
      <Control.IconButton
        data-testid="submitAddingNewOption"
        disabled={!!errorMessage || !value.trim()}
        icon={<CheckIcon />}
        kind="positive"
        onClick={onSubmitHandler}
        shape="round"
        size="small"
        variant="outlined"
      />
      <Control.IconButton
        data-testid="cancelAddingNewOption"
        icon={<CrossIcon />}
        kind="secondary"
        onClick={onCancel}
        shape="round"
        size="small"
        variant="outlined"
      />
    </div>
  );
}
